import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {errorUiTexts, adminUiTexts} from 'data/ui-texts';
import {scenarioIds, scenariosData} from 'data/scenarios-data';
import {discussionQuestions} from 'data/discussion-data';
import Button from 'components/ui/button/button';
import './popup-create-school.scss';

const PopupCreateSchool = ({schools, createSchool, closePopup}) => {
	/* Input: name, email, password, scenario ids */
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [selectedScenarioIds, setSelectedScenarioIds] = useState(JSON.parse(JSON.stringify(scenarioIds)));
	const [scenarioQrUrls, setScenarioQrUrls] = useState([
		{scenarioId: 'game1', qrUrl: scenariosData['game1'].defaultQrUrl},
		{scenarioId: 'game2', qrUrl: scenariosData['game2'].defaultQrUrl},
		{scenarioId: 'game3', qrUrl: scenariosData['game3'].defaultQrUrl},
		{scenarioId: 'game4', qrUrl: scenariosData['game4'].defaultQrUrl},
		{scenarioId: 'game5', qrUrl: scenariosData['game5'].defaultQrUrl}
	]);

	const [questions, setQuestions] = useState(JSON.parse(JSON.stringify(discussionQuestions)));

	const [facilitatorSurveyLink, setFacilitatorSurveyLink] = useState(null);	

	/* Loading status */
	const [isLoading, setIsLoading] = useState(false);

	/* Error message */
	const [errorMessage, setErrorMessage] = useState(null);

	/**
	 * Handles form input
	 * @param {object} event 
	 */
	const handleInput = (event) => {
		setErrorMessage(null);
		if (event.target.name === 'name') {
			setName(event.target.value);
		} else if (event.target.name === 'email') {
			setEmail(event.target.value);
		} else if (event.target.name === 'password') {
			setPassword(event.target.value);
		} else if (event.target.name.includes('question')) {
			const questionIdArr = event.target.name.split('-');
			if (questionIdArr.length === 2) {
				const questionIndex = parseInt(questionIdArr[1]);
				if (questions.length > questionIndex) {
					const newQuestions = JSON.parse(JSON.stringify(questions));
					newQuestions[questionIndex].text = event.target.value;
					setQuestions(newQuestions);
				}
			}
		} else if (event.target.name === 'facilitatorSurveyLink') {
			setFacilitatorSurveyLink(event.target.value);
		}
	};

	/**
	 * Toggle if a question is unlimited
	 * @param {number} questionIndex 
	 */
	const toggleQuestionLimit = (questionIndex) => {
		if (questions.length > questionIndex) {
			const newQuestions = JSON.parse(JSON.stringify(questions));
			newQuestions[questionIndex].isUnlimited = (newQuestions[questionIndex].isUnlimited === true ? false : true);
			setQuestions(newQuestions);
		}
	};

	/** Add questions */
	const addQuestion = () => {
		let newQuestions = JSON.parse(JSON.stringify(questions));
		let newId = newQuestions.length + 1;
		let newIdIsValid = false;
		while (!newIdIsValid) {
			// eslint-disable-next-line no-loop-func
			if (newQuestions.some((q) => {return q.id === newId;})) {
				newId += 1;
			} else {
				newIdIsValid = true;
			}
		}
		newQuestions.push({id: newId, text: ''});
		setQuestions(newQuestions);
		
	};
	/**
	 * Delete questions
	 * @param {number} questionIndex 
	 */
	const deleteQuestion = (questionIndex) => {
		let newQuestions = JSON.parse(JSON.stringify(questions));
		if (newQuestions.length > questionIndex) {
			newQuestions.splice(questionIndex, 1);
			setQuestions(newQuestions);
		}
	};

	/**
	 * Reset questions
	 */
	const resetQuestions = () => {
		setQuestions(JSON.parse(JSON.stringify(discussionQuestions)));
	};

	/**
	 * Toggle scenario id
	 * @param {string} scenarioId 
	 */
	const toggleScenarioId = (scenarioId) => {
		let newSelectedScenarioIds = JSON.parse(JSON.stringify(selectedScenarioIds));
		const scenarioIndex = newSelectedScenarioIds.indexOf(scenarioId);
		if (scenarioIndex >= 0) {
			newSelectedScenarioIds.splice(scenarioIndex, 1);
		} else {
			newSelectedScenarioIds.push(scenarioId);
		}
		setSelectedScenarioIds(newSelectedScenarioIds);
	};

	/**
	 * Custom scenario qr urls
	 * @param {string} scenarioId 
	 * @param {object} e 
	 */
	const updateScenarioQrUrl = (scenarioId, e) => {
		const newScenarioQrUrls = JSON.parse(JSON.stringify(scenarioQrUrls));
		const scenarioIndex = newScenarioQrUrls.findIndex((s) => {
			return s.scenarioId === scenarioId;
		});
		if (scenarioIndex >= 0) {
			newScenarioQrUrls[scenarioIndex].qrUrl = e.target.value;
			setScenarioQrUrls(newScenarioQrUrls);
		}

	};

	/**
	 * Create school
	 */
	const handleCreateSchool = () => {
		if (isLoading) return;

		setIsLoading(true);
		setErrorMessage(null);

		let newErrMsg = null;

		/* Check input */
		if (!name || !email || !password) {
			newErrMsg = errorUiTexts.emptyFields;
		/* Check school name */
		} else if (schools && schools.length > 0) {
			if (schools.some((s) => {
				return s.name.trim().toLowerCase() === name.trim().toLowerCase();
			})) {
				newErrMsg = errorUiTexts.schoolNameExists;
			} else {
			/* Check scenario urls */
				selectedScenarioIds.forEach((scenarioId) => {
					const scenarioQrUrl = scenarioQrUrls.find((s) => {return s.scenarioId === scenarioId;});
					if (scenarioQrUrl) {
						if (!scenarioQrUrl.qrUrl || scenarioQrUrl.qrUrl.length === 0) {
							newErrMsg = errorUiTexts.noQRUrl;
						}
					}
				});
			}
		}

		if (newErrMsg) {
			setErrorMessage(newErrMsg);
			setIsLoading(false);
		} else {
			const validQuestions = questions.filter((q) => {return q.text && q.text.length > 0;});
			createSchool(
				name, 
				email, 
				password, 
				selectedScenarioIds,
				scenarioQrUrls,
				validQuestions,
				facilitatorSurveyLink
			).then((response) => {
				if (response.status === 'success') {
					closePopup();
				} else {
					setIsLoading(false);
					console.error(response);
				}
			});
		}
	};

	return (
		<div className='PopupCreateSchool' onClick={() => {closePopup();}}>
			<div className='PopupCreateSchool-window' onClick={(e) => {e.stopPropagation();}}>
				<div className='PopupCreateSchool-title'>
					<span>{adminUiTexts.createSchoolPopup.title}</span>
				</div>
				<form className='PopupCreateSchool-form' onSubmit={handleCreateSchool}>
					<div className="PopupCreateSchool-columns">
						<div className="PopupCreateSchool-column left">
							<div className={'PopupCreateSchool-input'}>
								<div className='PopupCreateSchool-inputTitle'>
									<span>{adminUiTexts.createSchoolPopup.schoolName}</span>
								</div>
								<input
									name='name'
									type='text'
									placeholder={adminUiTexts.createSchoolPopup.school}
									autoComplete='section-inputAnswer name'
									value={name ? name : ''}
									onChange={(event)=>{handleInput(event);}}
								/>
							</div>
							<div className={'PopupCreateSchool-input'}>
								<div className='PopupCreateSchool-inputTitle'>
									<span>{adminUiTexts.createSchoolPopup.email}</span>
								</div>
								<input
									name='email'
									type='text'
									placeholder={adminUiTexts.createSchoolPopup.email}
									autoComplete='section-inputAnswer email'
									value={email ? email : ''}
									onChange={(event)=>{handleInput(event);}}
								/>
							</div>
							<div className={'PopupCreateSchool-input'}>
								<div className='PopupCreateSchool-inputTitle'>
									<span>{adminUiTexts.createSchoolPopup.password}</span>
								</div>
								<input
									name='password'
									type='text'
									placeholder={adminUiTexts.createSchoolPopup.password}
									autoComplete='section-inputAnswer password'
									value={password ? password : ''}
									onChange={(event)=>{handleInput(event);}}
								/>
							</div>
							<div className="PopupCreateSchool-input scenarios">
								<div className='PopupCreateSchool-inputTitle'>
									<span>{adminUiTexts.createSchoolPopup.games}</span>
								</div>
								<div className="PopupCreateSchool-scenarios">
									{scenarioIds.map((scenarioId) => {
										const scenarioData = scenariosData[scenarioId];
										if (!scenarioData) return null;

										const scenarioQrUrl = scenarioQrUrls.find((s) => {
											return s.scenarioId === scenarioId;
										});
										return (
											<div 
												key={scenarioId} 
												className={'PopupCreateSchool-scenario' + 
													(selectedScenarioIds.includes(scenarioId) ? ' selected' : '')}
											>
												<div 
													className="PopupCreateSchool-scenarioName"
													onClick={() => {toggleScenarioId(scenarioId);}}
												>
													<span>{scenarioData.title}</span>
												</div>
												<div className="PopupCreateSchool-scenarioQrUrl">
													<input
														disabled={!selectedScenarioIds.includes(scenarioId)}
														name={'qrUrl-' + scenarioId}
														type='text'
														placeholder={adminUiTexts.createSchoolPopup.qrUrl}
														autoComplete='section-inputAnswer qrUrl'
														value={scenarioQrUrl && scenarioQrUrl.qrUrl 
															? scenarioQrUrl.qrUrl : ''}
														onChange={(event)=>{updateScenarioQrUrl(scenarioId, event);}}
													/>
												</div>
											</div>
										);
									})}
								</div>
							</div>
							<div className="PopupCreateSchool-facilitatorSurveyLink">
								<div className={'PopupCreateSchool-input facilitatorSurveyLink'}>
									<div className='PopupCreateSchool-inputTitle'>
										<span>{adminUiTexts.createSchoolPopup.facilitatorSurvey}</span>
									</div>
									<input
										name='facilitatorSurveyLink'
										type='text'
										autoComplete='section-inputAnswer facilitatorSurvey'
										value={facilitatorSurveyLink ? facilitatorSurveyLink : ''}
										onChange={(event)=>{handleInput(event);}}
									/>
								</div>
							</div>
						</div>
						<div className="PopupCreateSchool-column right">
							<div className="PopupCreateSchool-questionsTitle">
								<span>{adminUiTexts.createSchoolPopup.questions} ({questions.length})</span>
							</div>
							<div className="PopupCreateSchool-addQuestionBtn" onClick={addQuestion} />
							<div className="PopupCreateSchool-resetQuestionsBtn" onClick={resetQuestions} />
							<div className="PopupCreateSchool-questions">
								{questions.map((q, index) => {
									return (
										<div key={index} className="PopupCreateSchool-question">
											<div className="PopupCreateSchool-toggleWrap">
												<div 
													className={'PopupCreateSchool-questionUnlimitedToggle' 
														+ (q.isUnlimited ? ' checked' : '')} 
													onClick={() => {toggleQuestionLimit(index);}}
												/>
											</div>
											<input 
												name={'question-' + index}
												value={q.text ? q.text : ''}
												onChange={(event) => {handleInput(event);}}
											/>
											<div 
												className="PopupCreateSchool-deleteQuestionBtn" 
												onClick={() => {deleteQuestion(index);}}
											/>
										</div>
									);
								})}
							</div>
						</div>
					</div>
				
					<div className="PopupCreateSchool-submitButton">
						<Button
							isDisabled={isLoading}
							text={adminUiTexts.createSchoolPopup.create}
							classes={['login']}
							onClick={() => {handleCreateSchool();}}
						/>
					</div>
				</form>

				{errorMessage &&
					<div className='PopupCreateSchool-errorMessage'>
						<span>{errorMessage}</span>
					</div>
				}
			</div>
		</div>
	);
};

PopupCreateSchool.propTypes = {
	schools: PropTypes.array,
	createSchool: PropTypes.func.isRequired,
	closePopup: PropTypes.func.isRequired,
};

export default PopupCreateSchool;